import { useCallback } from 'react';
import { pick } from 'js/utils/ramda.util';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { useComponentConfig } from 'js/hooks/component/useComponentConfig';

import * as server from '../server';
import * as helpers from '../helpers';
import * as actions from '../store/actions';
import { LAST_SIX_MONTHS, DEFAULT_PAGE_SIZE } from '../constants';
import { useDynamicReducerDispatch, useDynamicReducerSelector } from '../store';

export const useFetchHistory = () => {
    const isMounted = useIsMounted();
    const { tkPrefix } = useComponentConfig();
    const dispatch = useDynamicReducerDispatch();
    const {
        filters: { period, status, type },
        page: { pageId, secondBulk },
        startDate, endDate, isPreventFetchByAnotherPeriod
    } = useDynamicReducerSelector(pick([
        'filters', 'page', 'startDate', 'endDate', 'isPreventFetchByAnotherPeriod'
    ]));

    return useCallback(() => {
        if (!isMounted() || isPreventFetchByAnotherPeriod) {
            return;
        }

        dispatch(actions.updateProperty('isHistoryLoading', true));

        const { requestStartDate, requestEndDate } = helpers.getRequestDates({ period, startDate, endDate });
        const isAutoFetchHistory = secondBulk && period === LAST_SIX_MONTHS;
        const { startDate: startDateSecondBulk, endDate: endDateSecondBulk } = helpers.getSecondBulkForSixMonthsDates();

        const params = {
            type,
            status,
            pageId: pageId || 0,
            pageSize: DEFAULT_PAGE_SIZE,
            endDate: isAutoFetchHistory ? endDateSecondBulk : requestEndDate,
            startDate: isAutoFetchHistory ? startDateSecondBulk : requestStartDate
        };

        server.getTransactionHistory(params)
            .then(response => {
                const { data: { transactions = [], pagingResponseParams = {} } } = response;
                const data = helpers.parseHistoryData({ transactions, pagingResponseParams });

                isMounted() && dispatch(actions.updateHistoryData(data));
            })
            .catch(error => isMounted() && dispatch(actions.fetchFail(helpers.parseError(error, tkPrefix))))
            .finally(() => isMounted() && dispatch(actions.updateProperty('isHistoryLoading', false)));
    }, [
        type, period, status, pageId, tkPrefix, dispatch,
        isMounted, secondBulk, startDate, endDate, isPreventFetchByAnotherPeriod
    ]);
};

